import { useState, useEffect } from "react";
import {
  processSubscriptions,
  processActiveSubscriptions,
  processCanceledSubscriptions,
} from "../utils/subscriptionHandlers";

/**
 * Custom hook for managing subscription table state and filters
 * @param {Array} totalDataValues - Array of subscription arrays for total subscriptions
 * @param {Array} totalActiveDataValues - Array of subscription arrays for active subscriptions
 * @param {Array} totalCanceledDataValues - Array of subscription arrays for canceled subscriptions
 * @returns {Object} Table state and handlers
 */
export const useSubscriptionTable = (
  totalDataValues,
  totalActiveDataValues,
  totalCanceledDataValues
) => {
  // Calculate initial date ranges
  const getInitialRange = (days = 6) => {
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    return {
      startDate,
      endDate,
      key: "range1",
    };
  };

  // State for recurring subscriptions table
  const [totalData, setTotalData] = useState([]);
  const [selectionRange, setSelectionRange] = useState(getInitialRange(6));
  const [openRangeDialog, setOpenRangeDialog] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState("ALL");

  // State for active subscriptions table
  const [activeData, setActiveData] = useState([]);
  const [selectionRangeActive, setSelectionRangeActive] = useState(
    getInitialRange(6)
  );
  const [openRangeDialogActive, setOpenRangeDialogActive] = useState(false);
  const [selectedPlansActive, setSelectedPlansActive] = useState("ALL");

  // State for canceled subscriptions table
  const [totalCanceledData, setTotalCanceledData] = useState([]);
  const [selectionRangeCanceled, setSelectionRangeCanceled] = useState(
    getInitialRange(14)
  );
  const [openRangeDialogCanceled, setOpenRangeDialogCanceled] = useState(false);
  const [selectedPlansCanceled, setSelectedPlansCanceled] = useState("ALL");

  // Handler functions
  const handleSubscriptions = (values, range, plan) => {
    const totalsResult = processSubscriptions(values, range, plan);
    setTotalData(totalsResult);
  };

  const handleActiveSubscriptions = (values, range, plan) => {
    const totalsResult = processActiveSubscriptions(values, range, plan);
    setActiveData(totalsResult);
  };

  const handleSubscriptionsCanceled = (values, range, plan) => {
    const totalsResult = processCanceledSubscriptions(values, range, plan);
    setTotalCanceledData(totalsResult);
  };

  const handleSelectRange = (ranges) => {
    setSelectionRange(ranges.range1);
    handleSubscriptions(totalDataValues, ranges.range1, selectedPlans);
  };

  const handleSubsPlan = (plan) => {
    setSelectedPlans(plan.target.value);
    handleSubscriptions(totalDataValues, selectionRange, plan.target.value);
  };

  const handleSelectRangeActive = (ranges) => {
    setSelectionRangeActive(ranges.range1);
    handleActiveSubscriptions(
      totalActiveDataValues,
      ranges.range1,
      selectedPlansActive
    );
  };

  const handleSubsPlanActive = (plan) => {
    setSelectedPlansActive(plan.target.value);
    handleActiveSubscriptions(
      totalActiveDataValues,
      selectionRangeActive,
      plan.target.value
    );
  };

  const handleSelectRangeCanceled = (ranges) => {
    setSelectionRangeCanceled(ranges.range1);
    handleSubscriptionsCanceled(
      totalCanceledDataValues,
      ranges.range1,
      selectedPlansCanceled
    );
  };

  const handleSubsPlanCanceled = (plan) => {
    setSelectedPlansCanceled(plan.target.value);
    handleSubscriptionsCanceled(
      totalCanceledDataValues,
      selectionRangeCanceled,
      plan.target.value
    );
  };

  // Initialize tables when data is available
  useEffect(() => {
    if (totalDataValues?.length > 0) {
      handleSubscriptions(totalDataValues, selectionRange, selectedPlans);
    }

    if (totalActiveDataValues?.length > 0) {
      handleActiveSubscriptions(
        totalActiveDataValues,
        selectionRangeActive,
        selectedPlansActive
      );
    }

    if (totalCanceledDataValues?.length > 0) {
      handleSubscriptionsCanceled(
        totalCanceledDataValues,
        selectionRangeCanceled,
        selectedPlansCanceled
      );
    }
  }, []);

  // Update tables when data changes
  useEffect(() => {
    if (totalDataValues?.length > 0) {
      handleSubscriptions(totalDataValues, selectionRange, selectedPlans);
    }
  }, [totalDataValues]);

  useEffect(() => {
    if (totalActiveDataValues?.length > 0) {
      handleActiveSubscriptions(
        totalActiveDataValues,
        selectionRangeActive,
        selectedPlansActive
      );
    }
  }, [totalActiveDataValues]);

  useEffect(() => {
    if (totalCanceledDataValues?.length > 0) {
      handleSubscriptionsCanceled(
        totalCanceledDataValues,
        selectionRangeCanceled,
        selectedPlansCanceled
      );
    }
  }, [totalCanceledDataValues]);

  return {
    // Recurring subscriptions
    totalData,
    selectionRange,
    openRangeDialog,
    setOpenRangeDialog,
    selectedPlans,
    handleSelectRange,
    handleSubsPlan,
    // Active subscriptions
    activeData,
    selectionRangeActive,
    openRangeDialogActive,
    setOpenRangeDialogActive,
    selectedPlansActive,
    handleSelectRangeActive,
    handleSubsPlanActive,
    // Canceled subscriptions
    totalCanceledData,
    selectionRangeCanceled,
    openRangeDialogCanceled,
    setOpenRangeDialogCanceled,
    selectedPlansCanceled,
    handleSelectRangeCanceled,
    handleSubsPlanCanceled,
  };
};
